import { Injectable, inject } from '@angular/core';

import { SessionService } from '../../../core/services';

/**
 * Where a person is with the Playground tours: the first endpoint they open
 * walks them through it once; on every other endpoint's first visit the
 * Tour button is only marked, so they know that page has its own tour too.
 *
 * Kept per user on the backend (GET /me → user.preferences.playground_tours,
 * saved with PATCH /me/preferences). Falls back to this browser's
 * localStorage while nobody is signed in, or against a backend whose /me
 * doesn't send preferences at all.
 */
export interface TourProgress {
  /** The one automatic tour has run (on whichever endpoint came first). */
  autoShown: boolean;
  /** Endpoints whose tour was opened, e.g. ["/rag"]. */
  seen: string[];
  /** Endpoints opened at least once, e.g. ["/rag", "/answer"]. */
  visited: string[];
}

export type Arrival = 'start' | 'mark' | 'none';

/** The key under user.preferences. */
export const TOURS_PREF = 'playground_tours';
const LOCAL_KEY = 'mila.playground.tours';
/** The first local record: just the seen list. */
const LEGACY_KEY = 'mila.playground.tourSeen';

const EMPTY: TourProgress = { autoShown: false, seen: [], visited: [] };

// ── The rules (pure) ────────────────────────────────────────────────────

/** Opening an endpoint page: start the tour, mark the button, or neither;
 *  `next` has the visit recorded, so the mark shows only once. */
export function arrive(p: TourProgress, path: string): { next: TourProgress; action: Arrival } {
  const firstVisit = !p.visited.includes(path);
  const next = firstVisit ? { ...p, visited: [...p.visited, path] } : p;
  const action: Arrival = !p.autoShown ? 'start' : firstVisit && !p.seen.includes(path) ? 'mark' : 'none';
  return { next, action };
}

/** A tour was opened (automatically or from the button). */
export function withSeen(p: TourProgress, path: string): TourProgress {
  return {
    autoShown: true,
    seen: p.seen.includes(path) ? p.seen : [...p.seen, path],
    visited: p.visited.includes(path) ? p.visited : [...p.visited, path],
  };
}

/** Both records together: anything either has seen or visited counts. */
export function mergeProgress(a: TourProgress, b: TourProgress): TourProgress {
  return {
    autoShown: a.autoShown || b.autoShown,
    seen: union(a.seen, b.seen),
    visited: union(a.visited, b.visited),
  };
}

// ── Where it's kept ─────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class TourProgressService {
  private readonly session = inject(SessionService);

  /** What to do as an endpoint page opens; records the visit. */
  arriveAt(path: string): Arrival {
    const { next, action } = arrive(this.load(), path);
    this.save(next);
    return action;
  }

  markSeen(path: string): void {
    this.save(withSeen(this.load(), path));
  }

  /** The signed-in user's record. The first time it's read on a browser that
   *  still has a local one (from before the backend kept it), that one is
   *  folded in, saved for the user, and removed, so it can't carry over to
   *  whoever signs in here next. */
  load(): TourProgress {
    const prefs = this.session.user?.preferences;
    if (!prefs) return readLocal() ?? EMPTY;
    const remote = fromWire(prefs[TOURS_PREF]);
    const local = readLocal();
    if (!local) return remote;
    const merged = mergeProgress(remote, local);
    clearLocal();
    if (!sameProgress(merged, remote)) this.save(merged, remote);
    return merged;
  }

  /** To the backend when the user has preferences, else this browser.
   *  Nothing is sent when it hasn't changed. */
  private save(p: TourProgress, current = this.load()): void {
    if (sameProgress(p, current)) return;
    if (this.session.user?.preferences) {
      // The user's copy updates at once; a failed request just means it's
      // asked again on the next change.
      this.session.updatePreferences({ [TOURS_PREF]: toWire(p) }).subscribe({ error: () => {} });
    } else {
      writeLocal(p);
    }
  }
}

// ── Formats ─────────────────────────────────────────────────────────────

/** The backend's snake_case record → ours; anything unexpected reads as empty. */
export function fromWire(v: unknown): TourProgress {
  const o = v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  return { autoShown: !!o['auto_shown'], seen: strings(o['seen']), visited: strings(o['visited']) };
}

export function toWire(p: TourProgress): Record<string, unknown> {
  return { auto_shown: p.autoShown, seen: p.seen, visited: p.visited };
}

/** This browser's record, or the first one (a seen list), or null. */
function readLocal(): TourProgress | null {
  try {
    const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) ?? 'null');
    if (raw && typeof raw === 'object') {
      return { autoShown: !!raw.autoShown, seen: strings(raw.seen), visited: strings(raw.visited) };
    }
    const legacy = strings(JSON.parse(localStorage.getItem(LEGACY_KEY) ?? '[]'));
    // Someone who has seen a tour already had their automatic one.
    return legacy.length ? { autoShown: true, seen: legacy, visited: legacy } : null;
  } catch {
    return null;
  }
}

function writeLocal(p: TourProgress): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(p));
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* private mode etc.: it just won't be remembered */ }
}

function clearLocal(): void {
  try {
    localStorage.removeItem(LOCAL_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* nothing to clear */ }
}

function sameProgress(a: TourProgress, b: TourProgress): boolean {
  return a.autoShown === b.autoShown && a.seen.join() === b.seen.join() && a.visited.join() === b.visited.join();
}

function union(a: string[], b: string[]): string[] {
  return [...a, ...b.filter(x => !a.includes(x))];
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
